/**
 * Professional Color Contrast Calculation Utilities
 * 
 * Extracted from ClassicCaptionPage.tsx to provide professional-grade
 * color accessibility calculations for timeloom text rendering systems.
 * 
 * Features:
 * - W3C WCAG 2.1 compliant contrast ratio calculations
 * - Automatic color replacement for accessibility
 * - Professional luminance calculations using sRGB color space
 * - Color name to hex conversion utilities
 * - Contrast color selection (black/white optimization)
 * 
 * Usage:
 * - Text/background contrast validation
 * - Automatic accessible color selection
 * - Professional typography color systems
 */

/**
 * Color name to hex conversion for common color names
 * Supports basic color names used in design systems
 */
export function colorNameToHex(color: string): string {
    const colors: Record<string, string> = {
        black: "#000000",
        white: "#FFFFFF", 
        red: "#FF0000",
        green: "#008000",
        blue: "#0000FF",
        yellow: "#FFFF00",
        cyan: "#00FFFF",
        magenta: "#FF00FF",
        gray: "#808080",
        grey: "#808080", // British spelling
        orange: "#FFA500",
        purple: "#800080",
        pink: "#FFC0CB",
        brown: "#A52A2A",
        silver: "#C0C0C0",
        gold: "#FFD700",
        navy: "#000080",
        teal: "#008080",
        lime: "#00FF00",
        maroon: "#800000",
        olive: "#808000"
    };
    return colors[color.toLowerCase()] || color;
}

/**
 * Convert RGB values to relative luminance using W3C WCAG 2.1 formula
 * This follows the sRGB color space specification for accurate accessibility calculations
 * 
 * @param r Red component (0-255)
 * @param g Green component (0-255) 
 * @param b Blue component (0-255)
 * @returns Relative luminance value (0-1)
 */
export function rgbToLuminance(r: number, g: number, b: number): number {
    const a = [r / 255, g / 255, b / 255];
    for (let i = 0; i < 3; i++) {
        if (a[i] <= 0.03928) {
            a[i] /= 12.92;
        } else {
            a[i] = Math.pow((a[i] + 0.055) / 1.055, 2.4);
        }
    }
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

/**
 * Calculate contrast ratio between two luminance values
 * Returns ratio according to W3C WCAG 2.1 accessibility guidelines
 * 
 * @param luminance1 First color luminance (0-1)
 * @param luminance2 Second color luminance (0-1) 
 * @returns Contrast ratio (1-21, where 21 is maximum contrast)
 */
export function calculateContrastRatio(luminance1: number, luminance2: number): number {
    const light = Math.max(luminance1, luminance2);
    const dark = Math.min(luminance1, luminance2);
    return (light + 0.05) / (dark + 0.05);
}

/**
 * Convert hex color string to RGB values
 * Handles both 3-digit and 6-digit hex colors, with or without # prefix
 * 
 * @param hex Hex color string (e.g., "#FF0000", "FF0000", "#F00", "F00")
 * @returns RGB tuple [r, g, b] with values 0-255
 */
export function hexToRgb(hex: string): [number, number, number] {
    // Remove # if present
    if (hex.startsWith("#")) {
        hex = hex.slice(1);
    }
    
    // Handle 3-digit hex colors (e.g., "F00" -> "FF0000")
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return [r, g, b];
}

/**
 * Professional contrast checking with automatic accessible color replacement
 * 
 * Tests if two colors have sufficient contrast ratio (4.5:1 minimum for normal text)
 * If contrast is insufficient, provides best replacement (black or white)
 * 
 * @param fixedColor The color that cannot be changed (background, brand color, etc.)
 * @param changeableColor The color that can be adjusted for better contrast
 * @returns Object with contrast analysis and replacement recommendation
 */
export function checkContrast(fixedColor: string, changeableColor: string): {
    sufficientContrast: boolean;
    replacementColor: string;
    contrastRatio: number;
    recommendedColor: 'black' | 'white';
} {
    // Normalize color inputs
    const normalizedFixed = colorNameToHex(fixedColor);
    const normalizedChangeable = colorNameToHex(changeableColor);
    
    // Convert to RGB
    const [fixedR, fixedG, fixedB] = hexToRgb(normalizedFixed);
    const [changeableR, changeableG, changeableB] = hexToRgb(normalizedChangeable);

    // Calculate luminance for both colors
    const luminanceFixed = rgbToLuminance(fixedR, fixedG, fixedB);
    const luminanceChangeable = rgbToLuminance(changeableR, changeableG, changeableB);

    // Calculate contrast ratio
    const contrastRatio = calculateContrastRatio(luminanceFixed, luminanceChangeable);

    // W3C WCAG 2.1 AA standard: minimum 4.5:1 for normal text
    const minimumContrast = 4.5;
    
    if (contrastRatio >= minimumContrast) {
        return {
            sufficientContrast: true,
            replacementColor: changeableColor, // Keep original if sufficient
            contrastRatio,
            recommendedColor: luminanceFixed > 0.5 ? 'black' : 'white'
        };
    }

    // Insufficient contrast - test black and white for best replacement
    const luminanceBlack = rgbToLuminance(0, 0, 0);
    const luminanceWhite = rgbToLuminance(255, 255, 255);
    const contrastWithBlack = calculateContrastRatio(luminanceFixed, luminanceBlack);
    const contrastWithWhite = calculateContrastRatio(luminanceFixed, luminanceWhite);

    // Choose the color with better contrast
    const useBlack = contrastWithBlack > contrastWithWhite;
    const bestColor = useBlack ? 'black' : 'white';
    const bestContrastRatio = useBlack ? contrastWithBlack : contrastWithWhite;

    return {
        sufficientContrast: false,
        replacementColor: bestColor,
        contrastRatio: bestContrastRatio,
        recommendedColor: bestColor
    };
}

/**
 * Simple contrast color selection (optimized for white text preference)
 * 
 * Returns black or white based on luminance, with higher threshold to favor white text
 * This creates better readability on most backgrounds used in video content
 * 
 * @param hexColor Background color in hex format
 * @returns 'black' or 'white' for optimal contrast
 */
export function getContrastColor(hexColor: string): 'black' | 'white' {
    const normalizedColor = colorNameToHex(hexColor);
    const [r, g, b] = hexToRgb(normalizedColor);
    
    // Use consistent W3C WCAG luminance calculation
    const luminance = rgbToLuminance(r, g, b);
    
    // Use higher threshold (0.7) to favor white text for professional video appearance
    return luminance > 0.7 ? 'black' : 'white';
}

/**
 * Background type to CSS conversion utilities
 */
export type BackgroundType = 'none' | 'white' | 'colored' | 'translucent';

export function backgroundToCSS(background: BackgroundType, primaryColor: string): string {
    switch (background) {
        case 'none':
            return 'transparent';
        case 'white':
            return 'rgba(255, 255, 255, 1)';
        case 'colored':
            return primaryColor;
        case 'translucent':
            return 'rgba(0, 0, 0, 0.7)';
        default:
            return 'transparent';
    }
}

/**
 * Advanced color analysis for text/outline combinations
 * 
 * Analyzes and optimizes colors for text, outline, and background combinations
 * ensuring professional accessibility standards across all color relationships
 * 
 * @param outline Outline type configuration
 * @param textAnimation Animation type that may affect color usage
 * @param active Whether text is currently active/highlighted
 * @param background Background type configuration
 * @param currentSpokenWordColor Highlight color for active words
 * @param primaryColor Primary theme color
 * @returns Optimized colors for text and outline
 */
export function getOutlineAndTextColorForStyle(
    outline: 'none' | 'glow' | 'shadow' | '3d-shadow' | 'stroke' | '3d-stroke',
    textAnimation: string,
    active: boolean,
    background: BackgroundType,
    currentSpokenWordColor: string,
    primaryColor: string
): { outlineColor: string | undefined; textColor: string } {
    const hasOutline = outline === '3d-shadow' || outline === 'stroke' || outline === '3d-stroke';
    
    // Determine background color for contrast calculations
    let backgroundColor = background === 'none' ? undefined : 
        (background === "translucent" ? 'black' : 
        (background === 'white' ? 'white' : primaryColor));
    
    let outlineColor = hasOutline ? primaryColor : undefined;

    // Ensure outline contrasts with background
    if (outlineColor && backgroundColor) {
        if (outlineColor === backgroundColor) {
            outlineColor = getContrastColor(backgroundColor);
        }
        const backgroundToOutlineContrastAnalysis = checkContrast(backgroundColor, outlineColor);
        if (!backgroundToOutlineContrastAnalysis.sufficientContrast) {
            outlineColor = backgroundToOutlineContrastAnalysis.replacementColor;
        }
    }
    
    // Calculate text color
    let textColor = primaryColor;
    if (textColor === backgroundColor && backgroundColor === primaryColor) {
        textColor = getContrastColor(backgroundColor);
        if (outlineColor && outlineColor === textColor) {
            textColor = getContrastColor(outlineColor);
        }
    }
    if (textColor === outlineColor) {
        textColor = getContrastColor(outlineColor);
    }
    
    // Handle active state color adjustments
    if (!active) {
        return { outlineColor, textColor };
    } else {
        const isBoxAnimation = textAnimation === 'boxed' || textAnimation === 'karaoke_box' || textAnimation === 'reveal_box';
        const isHighlightAnimation = !isBoxAnimation && textAnimation !== 'none';
        
        if (isBoxAnimation) {
            if (!outlineColor) {
                const highlightToTextColorContrastAnalysis = checkContrast(currentSpokenWordColor, textColor);
                if (!highlightToTextColorContrastAnalysis.sufficientContrast) {
                    textColor = highlightToTextColorContrastAnalysis.replacementColor;
                }
            }
            return { outlineColor, textColor };
        }
        
        if (isHighlightAnimation) {
            textColor = currentSpokenWordColor;
            if (outlineColor) {
                const outlineToTextColorContrastAnalysis = checkContrast(outlineColor, textColor);
                if (!outlineToTextColorContrastAnalysis.sufficientContrast) {
                    outlineColor = currentSpokenWordColor;
                    textColor = getContrastColor(outlineColor);
                }
            }
        }
        
        return { outlineColor, textColor };
    }
}

/**
 * Utility functions for color validation and testing
 */

/**
 * Test if a color string is a valid hex color
 */
export function isValidHexColor(color: string): boolean {
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(color);
}

/**
 * Test if a color string is a valid color name
 */
export function isValidColorName(color: string): boolean {
    return colorNameToHex(color) !== color.toLowerCase();
}

/**
 * Validate and normalize any color input
 */
export function normalizeColor(color: string): string {
    if (isValidHexColor(color)) {
        return color.startsWith('#') ? color : `#${color}`;
    }
    if (isValidColorName(color)) {
        return colorNameToHex(color);
    }
    // Return black as fallback for invalid colors
    return '#000000';
}

/**
 * Calculate relative luminance for a color string (any format)
 */
export function getColorLuminance(color: string): number {
    const normalized = colorNameToHex(color);
    const [r, g, b] = hexToRgb(normalized);
    return rgbToLuminance(r, g, b);
}

/**
 * Export type definitions for TypeScript support
 */
export interface ColorContrastResult {
    sufficientContrast: boolean;
    replacementColor: string;
    contrastRatio: number;
    recommendedColor: 'black' | 'white';
}

export interface ColorAnalysis {
    luminance: number;
    contrastWithBlack: number;
    contrastWithWhite: number;
    recommendedTextColor: 'black' | 'white';
    accessibilityRating: 'AAA' | 'AA' | 'A' | 'FAIL';
}

/**
 * Comprehensive color analysis for accessibility reporting
 */
export function analyzeColor(color: string): ColorAnalysis {
    const luminance = getColorLuminance(color);
    const contrastWithBlack = calculateContrastRatio(luminance, rgbToLuminance(0, 0, 0));
    const contrastWithWhite = calculateContrastRatio(luminance, rgbToLuminance(255, 255, 255));
    const recommendedTextColor = contrastWithBlack > contrastWithWhite ? 'black' : 'white';
    
    // WCAG 2.1 accessibility ratings
    const bestContrast = Math.max(contrastWithBlack, contrastWithWhite);
    let accessibilityRating: ColorAnalysis['accessibilityRating'];
    if (bestContrast >= 7) {
        accessibilityRating = 'AAA'; // Enhanced contrast
    } else if (bestContrast >= 4.5) {
        accessibilityRating = 'AA';  // Standard contrast
    } else if (bestContrast >= 3) {
        accessibilityRating = 'A';   // Large text only
    } else {
        accessibilityRating = 'FAIL'; // Insufficient contrast
    }
    
    return {
        luminance,
        contrastWithBlack,
        contrastWithWhite,
        recommendedTextColor,
        accessibilityRating
    };
}